// Kingston's drop-off window — one definition, shared by every message that
// tells a customer when to bring the car in.
//
// The appointment time on a booking is a SHOP time: when we plan to start on
// that car and how the bay is sequenced. Customers were reading it as an
// arrival time and trickling in across the day, which is the opposite of how
// the shop actually runs. So the quoted time stays (it's still in the
// reminder texts; the pre email's subject dropped it 2026-10-14), and this line says what to
// actually do: everyone drops off 9-10 AM regardless of the slot.
//
// 🚨 One string per channel, defined HERE and nowhere else. This sentence goes
// out on the welcome email, the confirmation text and both appointment
// reminders; the moment two of them disagree about the window, the customer
// picks whichever they read last and we get a car at 2 PM.

import { KINGSTON } from './sites';

export const SHOP_PHONE = '845-901-5573';

// Email — the pre (welcome) draft's paragraph on how the day runs.
// Rewritten 2026-10-14 (Ryder): there are no designated appointment times, only
// a set number of appointments per day; dropping off first does not mean being
// detailed first; customers who can't get a ride and must wait are expedited,
// but waiting is not recommended because most services run past 4 hours. The
// 9-10 AM window stays so the email agrees with the reminder texts.
// ⚠️ Because this says there are no set times, the pre email's SUBJECT no
// longer carries one for this audience either (see _buildPreContent).
export const DROPOFF_EMAIL_LINE =
  "A quick note on how our days work: we don't book set appointment times. Instead, we take a "
  + 'limited number of vehicles each day, and we ask everyone to drop off between 9:00 and 10:00 AM. '
  + "Being the first to drop off doesn't mean your vehicle will be the first one detailed, since we "
  + 'plan the day around each job.\n\n'
  + "If you can't get a ride and need to wait at the shop, let us know and we'll do our best to "
  + "move your vehicle up. That said, we don't recommend waiting: most of our services take more "
  + 'than 4 hours. If a morning drop-off is tough for you, just call or text ' + SHOP_PHONE
  + " and we'll work something out.";

// SMS — same two facts (the window, and who to call), tightened because every
// character here is billed. Callers that already ended with a 'call or text
// 845-901-5573' tail drop it when they append this, rather than printing the
// number twice in one message.
// Softened 2026-10-06 to match the email.
// Rewritten 2026-10-14 with the email: no set times, first in isn't first
// done, and waiting isn't recommended. Kept short — every character is billed.
export const DROPOFF_SMS_LINE =
  "We don't book set times: please drop off 9-10 AM. First in isn't always first done, and most services run 4+ hours, so we don't recommend waiting. "
  + "Need to wait or can't do mornings? Call or text " + SHOP_PHONE + '.';

// Does a CUSTOMER-facing message quote the appointment's start time?
//
// No, for everyone we know about (Ryder, 2026-10-14): Kingston tells customers
// there are no set appointment times, only a set number per day, so quoting
// one would contradict it; Albany and every dealership job already withheld
// it (see _tellCustomerTheTime in apiSupabase.js). The time is still on every
// STAFF surface — the schedule, job cards, checklists. Pure, so api/ and the
// SPA can share it. An unknown site falls through to true, i.e. the old
// behaviour, rather than silently hiding something.
export function customerSeesTime(job) {
  if (!job) return false;
  if (String(job.dealer || '').trim()) return false;
  const site = String(job.site || KINGSTON);
  if (site === 'albany') return false;
  return !dropoffWindowApplies(job);
}

// Who hears about the window.
//
// Kingston only — it is Kingston's bay schedule, and Albany runs its own day
// out of the dealership. A blank site is Kingston, matching siteMeta().
//
// ⚠️ Never on a DEALERSHIP job, at either shop. That car is driven over by the
// dealer once their own work is done; its owner is not the person dropping it
// off and cannot act on a drop-off window at all. Same reason the deposit block
// and the quoted time are held back from them.
export function dropoffWindowApplies(job) {
  if (!job) return false;
  if (String(job.dealer || '').trim()) return false;
  return String(job.site || KINGSTON) === KINGSTON;
}

// ===========================================================================
// Expected drop-off — what staff pick instead of an appointment time
// (Ryder, 2026-10-14). Stored on bookings.dropoff / dropoff_min
// (20261014120000). A day holds up to BOOKING_DAY_CAP jobs; nobody picks an
// hour. start_time still exists underneath (reminders, bay order, Pool sync,
// dealer free/busy all read it) and new bookings stamp DEFAULT_START_MIN there.
// ===========================================================================
export const DEFAULT_START_MIN = 540; // 9:00 — never shown to anyone

export const DROPOFF_OPTIONS = [
  { key: 'early',    label: 'Before open', short: 'Before open', hint: 'Night before or before 9 AM' },
  { key: 'standard', label: '9–10 AM',     short: '9–10 AM',     hint: 'The normal drop-off window' },
  { key: 'later',    label: 'Later',       short: 'Later',       hint: 'Customer said they will arrive later' },
  { key: 'waiting',  label: 'Waiting at shop', short: 'Waiting', hint: 'Staying while we work — move it up' },
];
export const DROPOFF_KEYS = DROPOFF_OPTIONS.map((o) => o.key);

// NULL (every booking made before this existed) reads as the normal window.
export function dropoffKey(job) {
  const k = String((job && job.dropoff) || '').trim();
  return DROPOFF_KEYS.includes(k) ? k : 'standard';
}

// 🚨 A DEALERSHIP job keeps a real slot. OTTO picks 9 / 12 / 3 in its portal,
// the approval keeps that time, and the car is driven over by the dealer once
// their own work is done — Kingston's "no set times, drop off 9–10" model is
// not their rule (Ryder, 2026-10-15: the Kingston time selector "carried over
// to otto which it was not supposed to"). So on a dealer job the drop-off
// label IS the slot, and the drop-off editor does not apply. One predicate,
// read by every card and by the approval panel, so the two cannot disagree.
export function hasOwnSlot(job) {
  return !!String((job && job.dealer) || '').trim();
}

// A booking's start as minutes from midnight, whichever shape it carries:
// mapBooking's 1899-epoch ISO (minToIso), "HH:mm", or a bare number. The ISO
// case mirrors isoOrHmToMin / bkgTimeToMin: NY is UTC-5 on that epoch, no DST.
export function startMinOf(job) {
  const v = job && (job.startTime != null ? job.startTime : job.start_time);
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v);
  const hm = s.match(/^(\d{1,2}):(\d{2})/);
  if (hm) return (+hm[1]) * 60 + (+hm[2]);
  if (s.includes('T')) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return ((d.getUTCHours() * 60 + d.getUTCMinutes()) - 300 + 1440) % 1440;
    return null;
  }
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
}

export function minToClock(min) {
  const m = Number(min);
  if (!Number.isFinite(m)) return '';
  const h = Math.floor(m / 60), mm = m % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return h12 + ':' + String(mm).padStart(2, '0') + ' ' + (h >= 12 ? 'PM' : 'AM');
}

// "Drop-off: 9–10 AM" / "Later · 1:30 PM" / "Waiting at shop" — staff-facing.
export function dropoffLabel(job, { short = false } = {}) {
  // A dealer job's "drop-off" is its slot — see hasOwnSlot.
  if (hasOwnSlot(job)) {
    const m = startMinOf(job);
    // minToClock(null) reads Number(null) as 0 → "12:00 AM"; guard it here.
    const t = m == null ? '' : minToClock(m);
    return t || (short ? 'Slot' : 'Dealer slot');
  }
  const k = dropoffKey(job);
  const o = DROPOFF_OPTIONS.find((x) => x.key === k);
  if (k === 'later') {
    const t = minToClock(job && job.dropoffMin != null ? job.dropoffMin : job && job.dropoff_min);
    return t ? (short ? t : 'Later · ' + t) : o.label;
  }
  return short ? o.short : o.label;
}

// Order within a day: waiting first (they are sitting in the lobby), then
// before-open, the normal window, then stated later arrivals by their time.
export function dropoffRank(job) {
  // A dealer job sorts by its slot, among the stated later arrivals.
  if (hasOwnSlot(job)) {
    const m = startMinOf(job);
    return 3 + (Number.isFinite(m) ? m / 10000 : 0.9999);
  }
  const k = dropoffKey(job);
  if (k === 'waiting') return 0;
  if (k === 'early') return 1;
  if (k === 'standard') return 2;
  const m = Number(job && (job.dropoffMin != null ? job.dropoffMin : job.dropoff_min));
  return 3 + (Number.isFinite(m) ? m / 10000 : 0.9999);
}
